import * as Lodash from 'lodash'
import * as React from 'react'

import ErrorMessage from '../../components/ErrorMessage'
import Loader from '../../components/Loader'

import DI from '../../app/di'
import { Creative } from '../../interfaces/entities/creative'
import { ICreativeService } from '../../interfaces/services/creatives'
import { usePrevious } from '../../utils/hooks'
import { translateWithUserLocale } from '../../utils/i18n'

import {
  Box,
  Button,
  Divider,
  Icon,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
  styled,
} from '@design/pt-ui'
import GeneratePreview from './GeneratePreview'

const valueToKeyValuePair = (placementType: any) => {
  return { value: placementType.value, label: placementType.label }
}

const Root = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  textAlign: 'left',
  marginTop: theme.spacing(1),
  marginRight: theme.spacing(1),
  width: '100%',
  top: '-10px',
  minHeight: '80vh',
  '@media (max-width: 960px)': {
    position: 'relative',
    width: 'auto',
    margin: 24,
  },
}))

const PreviewArea = styled('div')({
  marginTop: 25,
  overflowX: 'scroll',
})

export interface Props {
  creative: Creative
  disabled?: boolean
  preview?: boolean
}

export interface State {
  placement: string
  placements: Array<{ label: string; value: string }> | null
  loading: boolean
  error: string | null

  preview: string | null
  previewRef: HTMLDivElement | null
  previewLoading: boolean
}

export function CreativePreview(props: Props) {
  const [state, setState] = React.useState<State>({
    placements: null,
    placement: '',
    loading: false,
    error: null,

    preview: null,
    previewRef: null,
    previewLoading: false,
  })

  const prevCreative = usePrevious(props.creative)

  const changePlacement = (placement: string) => {
    setState((state) => ({ ...state, placement }))
  }

  const handlePlacementChange = (event: { target: { value: any } }) => {
    const placement = event.target.value
    changePlacement(placement)
  }

  const handlePreviewRequest = (creative: Creative) => {
    if (state.placement === '') {
      return
    }
    const creativeService = DI.get('CreativeService') as ICreativeService
    ;(async function preview() {
      setState((state) => ({
        ...state,
        loading: true,
        preview: null,
        error: null,
      }))

      const response = await creativeService.getPreview(
        creative,
        state.placement,
      )
      if (response.type === 'success') {
        setState((state) => ({
          ...state,
          preview: response.payload,
          loading: false,
          error: null,
        }))
      } else {
        setState((state) => ({
          ...state,
          loading: false,
          preview: null,
          error: response.payload.message,
        }))
      }
    })()
  }

  React.useEffect(() => {
    const creativeService = DI.get('CreativeService') as ICreativeService
    ;(async function init() {
      const placements = await creativeService.getPlacements(props.creative)

      setState((state) => {
        return {
          ...state,
          placements: placements.map(valueToKeyValuePair),
        }
      })
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (
      state.placement === '' &&
      state.placements != null &&
      state.placements.length > 0
    ) {
      changePlacement(state.placements[0].value)
    }
  }, [state.placement, state.placements])

  React.useEffect(() => {
    if (!props.disabled) {
      handlePreviewRequest(props.creative)
    }
  }, [props.disabled, state.placement]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!props.disabled) {
      const prev = Lodash.omit(prevCreative, ['comment', 'name'])
      const current = Lodash.omit(props.creative, ['comment', 'name'])

      if (!Lodash.isEqual(prev, current)) {
        handlePreviewRequest(props.creative)
      }
    }
  }, [prevCreative, props.creative, props.disabled]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (props.disabled) {
      setState((state) => ({
        ...state,
        preview: null,
      }))
    }
  }, [props.disabled])

  React.useEffect(() => {
    if (!state.previewRef) {
      return
    }
    const iframe = state.previewRef.querySelector('iframe')
    if (iframe === null) {
      return
    }

    setState((state) => ({ ...state, previewLoading: true }))

    const stopLoading = () =>
      setState((state) => ({ ...state, previewLoading: false }))
    iframe.onload = stopLoading

    // `onload` is attached after the cross-origin iframe is already in the DOM,
    // so it can fire before we subscribe and leave the loader stuck. Clear it
    // after a grace period as a fallback.
    const fallback = setTimeout(stopLoading, 4000)

    return () => {
      iframe.onload = null
      clearTimeout(fallback)
    }
  }, [state.previewRef, state.preview])

  const getContent = () => {
    if (state.error) {
      return <ErrorMessage style={{ padding: 25 }}>{state.error}</ErrorMessage>
    }

    if (state.loading || state.placements == null) {
      return (
        <div style={{ marginTop: 10, paddingTop: 30, paddingBottom: 30 }}>
          <Loader
            variant="flex"
            message={translateWithUserLocale('loaders.loading_preview_1')}
          />
        </div>
      )
    }

    if (state.preview) {
      return (
        <>
          {state.previewLoading ? (
            <div style={{ marginTop: 10, paddingTop: 30, paddingBottom: 30 }}>
              <Loader
                variant="flex"
                message={translateWithUserLocale('loaders.loading_preview_2')}
              />
            </div>
          ) : null}
          <PreviewArea
            id="cp-preview"
            sx={state.previewLoading ? { visibility: 'hidden' } : {}}
            ref={(ref: HTMLDivElement | null) => {
              if (state.previewRef === ref || ref == null) {
                return
              }

              setState((state) => {
                if (state.previewRef === ref) {
                  return state
                }

                return {
                  ...state,
                  previewRef: ref,
                }
              })
            }}
            dangerouslySetInnerHTML={{
              __html: state.preview || '',
            }}
          />
        </>
      )
    }

    return null
  }

  return (
    <Root>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '15px',
          marginTop: '8px',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6">
            {translateWithUserLocale(
              'creative_details.live_preview_from_facebook',
            )}
          </Typography>
          <Tooltip
            placement="top"
            title={translateWithUserLocale('preview.description_facebook')}>
            <Icon
              sx={{
                marginLeft: '15px',
                color: 'grey',
              }}>
              info
            </Icon>
          </Tooltip>
        </Box>
        {!props.preview && (
          <GeneratePreview
            activator={
              <Button variant="outlined" color="inherit">
                {translateWithUserLocale('preview.generate_preview')}
              </Button>
            }
            creative={props.creative}
          />
        )}
      </Box>
      <Select
        className="cp-creative-preview-placement-select"
        value={state.placement}
        name="placement"
        id="placement"
        variant="standard"
        onChange={handlePlacementChange}>
        {(state.placements || []).map((placement) => (
          <MenuItem value={placement.value} key={placement.value}>
            {placement.label}
          </MenuItem>
        ))}
      </Select>

      {!props.preview && (
        <Button
          className="cp-creative-preview-generate-preview"
          onClick={() => handlePreviewRequest(props.creative)}
          color="inherit"
          disabled={props.disabled || state.loading || state.placement === ''}
          sx={{ ml: '10px' }}
          autoFocus={false}>
          <Icon>refresh</Icon>
          {translateWithUserLocale('refresh')}
        </Button>
      )}

      <Divider component="h3" />
      {getContent()}
    </Root>
  )
}

export default CreativePreview
